import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Music,
  Shield,
  GitCommit,
  Star,
  Link2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Navigation from '@/components/Navigation';
import { useStore } from '@/lib/store';
import { useToast } from '@/hooks/use-toast';
import * as api from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { ManagePlaylistLinks } from '@/components/ManagePlaylistLinks';
import { getLeagueNameFromSlug, isValidSlug, getDisplayNameFromSlug } from '@/lib/league-slugs';
import type { League, CuratedPlaylist } from '@/lib/mock-data';

const Admin = () => {
  const { state } = useStore();
  const { currentUser } = state;
  const navigate = useNavigate();
  const { leagueSlug } = useParams<{ leagueSlug?: string }>();
  const { toast } = useToast();
  
  // Get league info from slug
  const leagueName = useMemo(() => {
    if (leagueSlug && isValidSlug(leagueSlug)) {
      return getLeagueNameFromSlug(leagueSlug);
    }
    return null;
  }, [leagueSlug]);
  
  const displayName = useMemo(() => {
    if (leagueSlug && isValidSlug(leagueSlug)) {
      return getDisplayNameFromSlug(leagueSlug);
    }
    return null;
  }, [leagueSlug]);
  
  const [currentLeague, setCurrentLeague] = useState<League | null>(null);
  const [allLeagues, setAllLeagues] = useState<League[]>([]); // All leagues for dropdowns
  const [curatedPlaylists, setCuratedPlaylists] = useState<CuratedPlaylist[]>([]);
  const [currentPlaylistId, setCurrentPlaylistId] = useState<string | null>(null);
  const [isUpdatingCurrentPlaylist, setIsUpdatingCurrentPlaylist] = useState(false);
  const [allRounds, setAllRounds] = useState<any[]>([]); // All rounds for dropdowns
  
  // SmartLink URL management state
  const [selectedPlaylistForSmartlink, setSelectedPlaylistForSmartlink] = useState<string>('');
  const [smartlinkUrl, setSmartlinkUrl] = useState<string>('');
  const [isUpdatingSmartlink, setIsUpdatingSmartlink] = useState(false);

  const [commitHash, setCommitHash] = useState<string>('');
  
  // Playlist links management state
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedLeagueName, setSelectedLeagueName] = useState<string>('');
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>('');
  const [selectedSeasonName, setSelectedSeasonName] = useState<string>('');
  const [selectedRoundIdForLinks, setSelectedRoundIdForLinks] = useState<string>('');
  const [selectedRoundName, setSelectedRoundName] = useState<string>('');
  const [seasons, setSeasons] = useState<any[]>([]);
  
  // Create curated playlist state
  const [newPlaylistName, setNewPlaylistName] = useState<string>('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState<string>('');
  const [selectedSeasonForNewPlaylist, setSelectedSeasonForNewPlaylist] = useState<string>('');
  const [isCreatingPlaylist, setIsCreatingPlaylist] = useState(false);

  // Get commit hash from build-time environment variable or git
  useEffect(() => {
    const envCommit = import.meta.env.VITE_COMMIT_HASH || import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA;
    if (envCommit) {
      setCommitHash(envCommit);
    } else {
      const windowCommit = (window as any).__COMMIT_HASH__;
      if (windowCommit) {
        setCommitHash(windowCommit);
      } else {
        setCommitHash('Unknown');
      }
    }
  }, []);

  // Fetch all leagues (for dropdowns and when no league slug is provided)
  useEffect(() => {
    const fetchAllLeagues = async () => {
      try {
        const leagues = await api.leagueAPI.getAllLeagues();
        setAllLeagues(leagues);
        
        // If no league slug, use first league as default for legacy behavior
        if (!leagueName && leagues.length > 0) {
          // Don't set currentLeague, but keep allLeagues populated for dropdowns
        }
      } catch (error) {
        console.error('[Admin] Error fetching all leagues:', error);
        setAllLeagues([]);
      }
    };
    
    fetchAllLeagues();
  }, [leagueName]);
  
  // Fetch league and data when leagueSlug is provided
  useEffect(() => {
    const fetchLeagueData = async () => {
      if (!leagueName) {
        // No league slug - show all leagues (legacy behavior)
        setCurrentLeague(null);
        setCuratedPlaylists([]);
        return;
      }
      
      try {
        // Find league by name from allLeagues or fetch fresh
        let league = allLeagues.find(l => l.name.toLowerCase() === leagueName.toLowerCase());
        
        if (!league) {
          // Fetch fresh if not in allLeagues yet
          const leagues = await api.leagueAPI.getAllLeagues();
          league = leagues.find(l => l.name.toLowerCase() === leagueName.toLowerCase());
        }
        
        if (!league) {
          console.warn(`[Admin] League "${leagueName}" not found`);
          setCurrentLeague(null);
          setCuratedPlaylists([]);
          return;
        }
        
        setCurrentLeague(league);
        
        // Fetch curated playlists for this league
        const playlists = await api.curatedPlaylistAPI.getCuratedPlaylists([league.id]);
        setCuratedPlaylists(playlists);
        
        // Get current playlist ID from database (query directly since League interface might not have it)
        const { data: leagueData, error: leagueDataError } = await supabase
          .from('leagues')
          .select('current_playlist_id')
          .eq('id', league.id)
          .single();
        
        if (!leagueDataError && leagueData) {
          setCurrentPlaylistId(leagueData.current_playlist_id || null);
        } else {
          setCurrentPlaylistId(null);
        }
        
        // Set default selected playlist for SmartLink management (only if not already set)
        if (playlists.length > 0 && selectedPlaylistForSmartlink === '') {
          setSelectedPlaylistForSmartlink(playlists[0].id);
          setSmartlinkUrl(playlists[0].smartlink_url || '');
        }
      } catch (error) {
        console.error('[Admin] Error fetching league data:', error);
        setCurrentLeague(null);
        setCuratedPlaylists([]);
      }
    };
    
    fetchLeagueData();
  }, [leagueName, allLeagues]);
  
  // Update SmartLink URL when selected playlist changes
  useEffect(() => {
    if (selectedPlaylistForSmartlink && curatedPlaylists.length > 0) {
      const playlist = curatedPlaylists.find(p => p.id === selectedPlaylistForSmartlink);
      if (playlist) {
        setSmartlinkUrl(playlist.smartlink_url || '');
      }
    }
  }, [selectedPlaylistForSmartlink, curatedPlaylists]);
  
  // Fetch all rounds for dropdowns
  useEffect(() => {
    const fetchAllRounds = async () => {
      try {
        if (currentLeague) {
          // Fetch rounds for current league only
          const { data: roundsData } = await supabase
            .from('rounds')
            .select('*')
            .eq('league_id', currentLeague.id)
            .order('round_number', { ascending: true });
          setAllRounds(roundsData || []);
        } else if (allLeagues.length > 0) {
          // Fetch rounds for all leagues
          const leagueIds = allLeagues.map(l => l.id);
          const { data: roundsData } = await supabase
            .from('rounds')
            .select('*')
            .in('league_id', leagueIds)
            .order('round_number', { ascending: true });
          setAllRounds(roundsData || []);
        } else {
          setAllRounds([]);
        }
      } catch (error) {
        console.error('[Admin] Error fetching rounds:', error);
        setAllRounds([]);
      }
    };
    
    fetchAllRounds();
  }, [currentLeague, allLeagues]);
  
  // Fetch seasons for playlist links management
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        if (currentLeague) {
          // Fetch seasons from current league only
          const leagueSeasons = await api.seasonAPI.getSeasonsByLeague(currentLeague.id);
          setSeasons(leagueSeasons);
        } else if (allLeagues.length > 0) {
          // Fetch seasons from all leagues (legacy behavior)
          const allSeasons: any[] = [];
          for (const league of allLeagues) {
            const leagueSeasons = await api.seasonAPI.getSeasonsByLeague(league.id);
            allSeasons.push(...leagueSeasons);
          }
          setSeasons(allSeasons);
        } else {
          setSeasons([]);
        }
      } catch (error) {
        console.error('Error fetching seasons:', error);
        setSeasons([]);
      }
    };
    
    fetchSeasons();
  }, [currentLeague, allLeagues]);

  // Handle updating SmartLink URL for curated playlist
  const handleUpdateSmartlink = async () => {
    if (!selectedPlaylistForSmartlink) {
      toast({
        title: 'Error',
        description: 'Please select a playlist.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsUpdatingSmartlink(true);
    try {
      await api.curatedPlaylistAPI.updateCuratedPlaylist(selectedPlaylistForSmartlink, {
        smartlink_url: smartlinkUrl.trim() || null,
      });
      
      toast({
        title: 'Success',
        description: 'SmartLink URL updated successfully.',
      });
      
      // Refresh curated playlists
      if (currentLeague) {
        const playlists = await api.curatedPlaylistAPI.getCuratedPlaylists([currentLeague.id]);
        setCuratedPlaylists(playlists);
        
        // Update the selected playlist's SmartLink URL in state
        const updatedPlaylist = playlists.find(p => p.id === selectedPlaylistForSmartlink);
        if (updatedPlaylist) {
          setSmartlinkUrl(updatedPlaylist.smartlink_url || '');
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update SmartLink URL',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingSmartlink(false);
    }
  };
  
  // Handle playlist selection change for SmartLink management
  const handlePlaylistSelectForSmartlink = (playlistId: string) => {
    setSelectedPlaylistForSmartlink(playlistId);
    const playlist = curatedPlaylists.find(p => p.id === playlistId);
    setSmartlinkUrl(playlist?.smartlink_url || '');
  };
  
  // Handle creating a new curated playlist
  const handleCreatePlaylist = async () => {
    if (!currentLeague) {
      toast({
        title: 'Error',
        description: 'No league selected.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!newPlaylistName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a playlist name.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsCreatingPlaylist(true);
    try {
      const newPlaylist = await api.curatedPlaylistAPI.createCuratedPlaylist({
        name: newPlaylistName.trim(),
        description: newPlaylistDescription.trim() || undefined,
        filters: selectedSeasonForNewPlaylist 
          ? { seasons: [seasons.find(s => s.id === selectedSeasonForNewPlaylist)?.name].filter(Boolean) }
          : {},
        created_by: currentUser?.id,
        season_id: selectedSeasonForNewPlaylist || undefined,
      });
      
      toast({
        title: 'Success',
        description: `Playlist "${newPlaylist.name}" created successfully.`,
      });
      
      // Refresh curated playlists
      const playlists = await api.curatedPlaylistAPI.getCuratedPlaylists([currentLeague.id]);
      setCuratedPlaylists(playlists);
      
      // Reset form
      setNewPlaylistName('');
      setNewPlaylistDescription('');
      setSelectedSeasonForNewPlaylist('');
      
      // Optionally select the new playlist
      setSelectedPlaylistForSmartlink(newPlaylist.id);
      setSmartlinkUrl(newPlaylist.smartlink_url || '');
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create playlist',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingPlaylist(false);
    }
  };
  
  // Handle updating current playlist
  const handleUpdateCurrentPlaylist = async (playlistId: string | null) => {
    if (!currentLeague) {
      toast({
        title: 'Error',
        description: 'No league selected.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsUpdatingCurrentPlaylist(true);
    try {
      // Update league's current_playlist_id
      await api.leagueAPI.updateLeague(currentLeague.id, {
        current_playlist_id: playlistId || null,
      });
      
      setCurrentPlaylistId(playlistId);
      toast({
        title: 'Success',
        description: playlistId 
          ? `Current playlist updated to "${curatedPlaylists.find(p => p.id === playlistId)?.name || 'Unknown'}"`
          : 'Current playlist cleared',
      });
      
      // Refresh league data
      const allLeagues = await api.leagueAPI.getAllLeagues();
      const updatedLeague = allLeagues.find(l => l.id === currentLeague.id);
      if (updatedLeague) {
        setCurrentLeague(updatedLeague);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update current playlist',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingCurrentPlaylist(false);
    }
  };
  
  // Check if user is admin
  if (!currentUser?.isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">You must be an administrator to access this page.</p>
          <Button onClick={() => navigate(leagueSlug ? `/${leagueSlug}` : '/')}>Back to Home</Button>
        </div>
      </div>
    );
  }
  
  // If league slug is required but not provided, redirect or show message
  if (leagueSlug && !isValidSlug(leagueSlug)) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">Invalid League</h1>
          <p className="text-muted-foreground mb-6">The league URL is invalid.</p>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
            <Button
            variant="ghost"
            onClick={() => navigate(leagueSlug ? `/${leagueSlug}` : '/')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to {displayName || 'Home'}
          </Button>

          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">
              Admin Panel
              {displayName && <span className="text-xl text-muted-foreground ml-2">- {displayName}</span>}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {currentLeague 
              ? `Manage deployment information, current playlist, and playlist links for ${displayName || currentLeague.name}.`
              : 'Manage deployment information and playlist links.'}
          </p>
        </div>

        {/* Commit Info */}
        <Card className="mb-8 border-muted">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <GitCommit className="h-4 w-4" />
              Deployment Info
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Commit Hash:</span>
                <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                  {commitHash.substring(0, 7) || 'Unknown'}
                </code>
              </div>
              {commitHash && commitHash !== 'Unknown' && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Full Hash:</span>
                  <code className="text-xs font-mono bg-muted px-2 py-1 rounded break-all">
                    {commitHash}
                  </code>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Create Curated Playlist - Only show if league is specified */}
        {currentLeague && (
          <Card className="mb-8 border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music className="h-5 w-5 text-primary" />
                Create Curated Playlist
              </CardTitle>
              <CardDescription>
                Create a new curated playlist for {displayName || currentLeague.name}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-playlist-name">Playlist Name *</Label>
                <Input
                  id="new-playlist-name"
                  placeholder="e.g., 1. Party On"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new-playlist-description">Description (Optional)</Label>
                <Input
                  id="new-playlist-description"
                  placeholder="e.g., Winter 2026 Round 1"
                  value={newPlaylistDescription}
                  onChange={(e) => setNewPlaylistDescription(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Filter by Season (Optional)</Label>
                <Select
                  value={selectedSeasonForNewPlaylist || 'none'}
                  onValueChange={(value) => setSelectedSeasonForNewPlaylist(value === 'none' ? '' : value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a season (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Universal Playlist)</SelectItem>
                    {seasons.map((season) => (
                      <SelectItem key={season.id} value={season.id}>
                        {season.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                onClick={handleCreatePlaylist}
                disabled={isCreatingPlaylist || !newPlaylistName.trim()}
                className="w-full sm:w-auto"
              >
                {isCreatingPlaylist ? 'Creating...' : 'Create Playlist'}
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Current Playlist Management - Only show if league is specified */}
        {currentLeague && (
          <Card className="mb-8 border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-primary" />
                Current Playlist
              </CardTitle>
              <CardDescription>
                Set the default playlist that will be selected when users visit the Playlists page for {displayName || currentLeague.name}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Current Playlist</Label>
                <Select
                  value={currentPlaylistId || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      handleUpdateCurrentPlaylist(null);
                    } else {
                      handleUpdateCurrentPlaylist(value);
                    }
                  }}
                  disabled={isUpdatingCurrentPlaylist}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a playlist..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (no default)</SelectItem>
                    {curatedPlaylists
                      .slice()
                      .sort((a, b) => {
                        const aIdx = a.sort_index ?? -Infinity;
                        const bIdx = b.sort_index ?? -Infinity;
                        if (aIdx !== bIdx) return bIdx - aIdx;
                        return a.name.localeCompare(b.name);
                      })
                      .map((playlist) => (
                      <SelectItem key={playlist.id} value={playlist.id}>
                        {playlist.name}
                        {playlist.description && ` - ${playlist.description}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {currentPlaylistId && (
                  <p className="text-sm text-muted-foreground">
                    Current: <strong>{curatedPlaylists.find(p => p.id === currentPlaylistId)?.name || 'Unknown'}</strong>
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* SmartLink URL Management - Only show if league is specified */}
        {currentLeague && curatedPlaylists.length > 0 && (
          <Card className="mb-8 border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary" />
                SmartLink URLs for Playlists
              </CardTitle>
              <CardDescription>
                Set SmartLink embed URLs for curated playlists. These will appear on the Playlists page when a playlist is selected.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Playlist</Label>
                <Select
                  value={selectedPlaylistForSmartlink}
                  onValueChange={handlePlaylistSelectForSmartlink}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a playlist..." />
                  </SelectTrigger>
                  <SelectContent>
                    {curatedPlaylists
                      .slice()
                      .sort((a, b) => {
                        const aIdx = a.sort_index ?? -Infinity;
                        const bIdx = b.sort_index ?? -Infinity;
                        if (aIdx !== bIdx) return bIdx - aIdx;
                        return a.name.localeCompare(b.name);
                      })
                      .map((playlist) => (
                      <SelectItem key={playlist.id} value={playlist.id}>
                        {playlist.name}
                        {playlist.description && ` - ${playlist.description}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedPlaylistForSmartlink && (
                <div className="space-y-2">
                  <Label htmlFor="smartlink-url">SmartLink Embed URL</Label>
                  <Input
                    id="smartlink-url"
                    placeholder="https://sdz.sh/.../widgetlinks"
                    value={smartlinkUrl}
                    onChange={(e) => setSmartlinkUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the SmartLinks embed URL (the <code>src</code> from the iframe embed code).
                    <br />
                    Example: <code className="text-xs">https://sdz.sh/Winter_2026_1_Party_On/widgetlinks</code>
                    <br />
                    <strong>Note:</strong> Use the URL ending in <code>/widgetlinks</code>, not the base URL.
                  </p>
                  <Button
                    onClick={handleUpdateSmartlink}
                    disabled={isUpdatingSmartlink}
                    className="w-full sm:w-auto"
                  >
                    {isUpdatingSmartlink ? 'Updating...' : 'Update SmartLink URL'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Playlist Links Management */}
        <Card className="mb-8 border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Music className="h-5 w-5 text-primary" />
              Playlist Links Management
            </CardTitle>
            <CardDescription>
              Add external playlist links (Spotify, Apple Music, Amazon Music, etc.) for leagues, seasons, and rounds.
              These links will appear on the Playlists page for users to access playlists on their preferred platform.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* League Links */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">League Playlist Links</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background mb-4"
                onChange={(e) => {
                  const leagueId = e.target.value;
                  if (leagueId) {
                    const league = currentLeague || state.leagues.find(l => l.id === leagueId);
                    if (league) {
                      setSelectedLeagueId(leagueId);
                      setSelectedLeagueName(league.name);
                    }
                  } else {
                    setSelectedLeagueId('');
                    setSelectedLeagueName('');
                  }
                }}
                defaultValue={currentLeague?.id || ""}
                value={selectedLeagueId || (currentLeague?.id || "")}
              >
                <option value="">Select a league...</option>
                {currentLeague ? (
                  <option key={currentLeague.id} value={currentLeague.id}>
                    {currentLeague.name}
                  </option>
                ) : (
                  allLeagues.map((league) => (
                    <option key={league.id} value={league.id}>
                      {league.name}
                    </option>
                  ))
                )}
              </select>
              {selectedLeagueId && (
                <ManagePlaylistLinks
                  playlistType="league"
                  playlistId={selectedLeagueId}
                  playlistName={selectedLeagueName}
                />
              )}
            </div>

            {/* Season Links */}
            <div className="space-y-2 pt-4 border-t">
              <Label className="text-base font-semibold">Season Playlist Links</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background mb-4"
                onChange={(e) => {
                  const seasonId = e.target.value;
                  if (seasonId) {
                    const season = seasons.find(s => s.id === seasonId);
                    if (season) {
                      setSelectedSeasonId(seasonId);
                      setSelectedSeasonName(season.name);
                    }
                  } else {
                    setSelectedSeasonId('');
                    setSelectedSeasonName('');
                  }
                }}
                defaultValue=""
              >
                <option value="">Select a season...</option>
                {seasons.map((season) => (
                  <option key={season.id} value={season.id}>
                    {season.name} ({allLeagues.find(l => l.id === season.league_id)?.name || 'Unknown League'})
                  </option>
                ))}
              </select>
              {selectedSeasonId && (
                <ManagePlaylistLinks
                  playlistType="season"
                  playlistId={selectedSeasonId}
                  playlistName={selectedSeasonName}
                />
              )}
            </div>

            {/* Round Links */}
            <div className="space-y-2 pt-4 border-t">
              <Label className="text-base font-semibold">Round Playlist Links</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background mb-4"
                onChange={(e) => {
                  const roundId = e.target.value;
                  if (roundId) {
                    const round = state.rounds.find(r => r.id === roundId);
                    if (round) {
                      setSelectedRoundIdForLinks(roundId);
                      setSelectedRoundName(`${round.theme} (Round ${round.round_number})`);
                    }
                  } else {
                    setSelectedRoundIdForLinks('');
                    setSelectedRoundName('');
                  }
                }}
                defaultValue=""
              >
                <option value="">Select a round...</option>
                {allRounds
                  .sort((a, b) => {
                    const leagueA = allLeagues.find(l => l.id === a.league_id)?.name || '';
                    const leagueB = allLeagues.find(l => l.id === b.league_id)?.name || '';
                    if (leagueA !== leagueB) return leagueA.localeCompare(leagueB);
                    return (a.round_number || 0) - (b.round_number || 0);
                  })
                  .map((round) => {
                    const league = allLeagues.find(l => l.id === round.league_id);
                    return (
                      <option key={round.id} value={round.id}>
                        {league?.name || 'Unknown'} - Round {round.round_number}: {round.theme}
                      </option>
                    );
                  })}
              </select>
              {selectedRoundIdForLinks && (
                <ManagePlaylistLinks
                  playlistType="round"
                  playlistId={selectedRoundIdForLinks}
                  playlistName={selectedRoundName}
                />
              )}
            </div>
          </CardContent>
        </Card>

      </main>
    </div>
  );
};

export default Admin;
